import React, { useCallback, useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowDownUp, Loader2, Wallet, RefreshCw } from "lucide-react";

const CURRENCIES = [
  { code: "USD", name: "US Dollar", symbol: "$", flag: "🇺🇸" },
  { code: "EUR", name: "Euro", symbol: "€", flag: "🇪🇺" },
  { code: "GBP", name: "British Pound", symbol: "£", flag: "🇬🇧" },
  { code: "INR", name: "Indian Rupee", symbol: "₹", flag: "🇮🇳" },
];

const SYM = Object.fromEntries(CURRENCIES.map((c) => [c.code, c.symbol]));

export default function Exchange() {
  const [balances, setBalances] = useState({ demo: {}, live: {} });
  const [mode, setMode] = useState("demo");
  const [bankingMode, setBankingMode] = useState("demo");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("EUR");
  const [amount, setAmount] = useState("100");
  const [quote, setQuote] = useState(null);
  const [rates, setRates] = useState({});
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [executing, setExecuting] = useState(false);

  const loadBalances = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet/balance");
      setBalances({ demo: data.balances_demo || {}, live: data.balances_live || {} });
      setBankingMode(data.banking_mode || "demo");
      setMode(data.banking_mode || "demo");
    } catch (e) { /* silent */ }
  }, []);

  const loadRates = useCallback(async () => {
    try {
      const { data } = await api.get("/wallet/fx-rates?base=USD");
      setRates(data.rates || {});
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    loadBalances();
    loadRates();
  }, [loadBalances, loadRates]);

  // Fetch a quote whenever inputs change
  useEffect(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || from === to) {
      setQuote(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoadingQuote(true);
      try {
        const { data } = await api.post("/wallet/exchange/quote", {
          from_ccy: from, to_ccy: to, amount: amt,
        });
        setQuote(data);
      } catch (e) {
        setQuote(null);
      } finally {
        setLoadingQuote(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [amount, from, to]);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const execute = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount.");
    if (from === to) return toast.error("Pick two different currencies.");
    const srcBalance = (balances[mode] || {})[from] || 0;
    if (srcBalance < amt) return toast.error(`Insufficient ${from} balance.`);
    setExecuting(true);
    try {
      const { data } = await api.post("/wallet/exchange", {
        from_ccy: from, to_ccy: to, amount: amt, mode,
      });
      toast.success(`Exchanged ${from} ${amt} → ${to} ${data.to.amount.toFixed(2)}`);
      setAmount("");
      await loadBalances();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExecuting(false);
    }
  };

  const srcBalance = (balances[mode] || {})[from] || 0;

  return (
    <AppLayout>
      <div className="text-eyebrow text-white/55">Multi-currency</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1 text-white" data-testid="exchange-heading">
        Currency exchange
      </h1>
      <p className="text-white/60 mt-2 text-sm max-w-2xl">
        Hold USD, EUR, GBP, and INR in one account. Convert between them at live mid-market rates (0.4% spread).
        No account to open — just pick the currencies.
      </p>

      {/* Balance strip */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="exchange-balances">
        {CURRENCIES.map((c) => {
          const bal = (balances[mode] || {})[c.code] || 0;
          return (
            <div key={c.code} className="fv-card p-4" data-testid={`balance-${c.code.toLowerCase()}`}>
              <div className="flex items-center justify-between">
                <div className="text-eyebrow text-white/55">{c.code}</div>
                <span className="text-xs text-white/40">{c.flag}</span>
              </div>
              <div className="font-display text-2xl font-bold mt-2 tabular-nums text-white">
                {c.symbol}{bal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-white/45 mt-1">{c.name}</div>
            </div>
          );
        })}
      </div>

      {/* Mode switch */}
      <div className="mt-6 flex items-center gap-2 text-xs">
        <span className="text-white/50">Mode</span>
        {["demo", "live"].map((m) => {
          const disabled = m === "demo" && bankingMode === "live";
          return (
            <button
              key={m}
              onClick={() => !disabled && setMode(m)}
              disabled={disabled}
              data-testid={`exchange-mode-${m}`}
              className={`px-3 py-1.5 rounded-full font-medium capitalize transition-all ${
                mode === m
                  ? "bg-[#10B981] text-[#052E20]"
                  : "bg-white/[0.06] text-white/70 hover:bg-white/[0.10]"
              } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              {m}
            </button>
          );
        })}
      </div>

      {/* Exchange form */}
      <div className="mt-6 fv-card p-6 md:p-8 max-w-2xl" data-testid="exchange-form">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
          <div>
            <Label className="text-xs text-white/60">From</Label>
            <div className="mt-1 flex gap-2">
              <select
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                data-testid="exchange-from-select"
                className="h-10 px-3 rounded-md bg-black/35 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{`${c.flag} ${c.code}`}</option>
                ))}
              </select>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                data-testid="exchange-amount"
                className="flex-1 font-mono"
                placeholder="0.00"
              />
            </div>
            <div className="text-xs text-white/45 mt-1.5 flex items-center gap-1">
              <Wallet className="w-3 h-3" />
              Available: <span className="font-mono text-white/70">{SYM[from]}{srcBalance.toFixed(2)}</span>
            </div>
          </div>
          <button
            onClick={swap}
            data-testid="exchange-swap"
            className="h-10 w-10 rounded-full bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] flex items-center justify-center text-white transition-all"
            title="Swap currencies"
          >
            <ArrowDownUp className="w-4 h-4" />
          </button>
          <div>
            <Label className="text-xs text-white/60">To</Label>
            <div className="mt-1 flex gap-2">
              <select
                value={to}
                onChange={(e) => setTo(e.target.value)}
                data-testid="exchange-to-select"
                className="h-10 px-3 rounded-md bg-black/35 border border-white/10 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#10B981]"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>{`${c.flag} ${c.code}`}</option>
                ))}
              </select>
              <div
                data-testid="exchange-to-amount"
                className="flex-1 h-10 px-3 rounded-md bg-black/35 border border-white/10 flex items-center font-mono text-white tabular-nums"
              >
                {loadingQuote ? "…" : quote ? quote.to_amount.toFixed(2) : "0.00"}
              </div>
            </div>
            <div className="text-xs text-white/45 mt-1.5">
              You'll receive · {quote ? <>rate <span className="font-mono text-white/70">{quote.rate.toFixed(4)}</span></> : "—"}
            </div>
          </div>
        </div>

        {quote && (
          <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between text-xs text-white/60">
            <div>
              <span className="font-mono text-white/70">1 {from} = {quote.rate.toFixed(6)} {to}</span>
              <span className="text-white/40 ml-2">(mid {quote.mid_rate.toFixed(4)}, spread {(quote.spread * 100).toFixed(2)}%)</span>
            </div>
            <button onClick={loadRates} className="hover:text-white flex items-center gap-1" data-testid="exchange-refresh-rates">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
        )}

        <Button
          onClick={execute}
          disabled={executing || !quote || srcBalance < parseFloat(amount || 0)}
          className="mt-5 w-full bg-[#10B981] hover:bg-[#059669] text-[#052E20] font-semibold h-11"
          data-testid="exchange-submit"
        >
          {executing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          Exchange {amount || 0} {from} → {quote ? quote.to_amount.toFixed(2) : "0.00"} {to}
        </Button>
      </div>

      {/* Live rates panel */}
      <div className="mt-8 fv-card p-6 md:p-7 max-w-2xl" data-testid="exchange-rates">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-eyebrow text-white/55">Live rates · USD base</div>
            <div className="text-xs text-white/45 mt-1">15-min cache · from exchangerate.host</div>
          </div>
          <button onClick={loadRates} className="text-xs text-white/55 hover:text-white flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {CURRENCIES.filter((c) => c.code !== "USD").map((c) => (
            <div key={c.code} className="rounded-lg bg-white/[0.03] border border-white/10 p-3">
              <div className="text-xs text-white/60">{c.flag} 1 USD</div>
              <div className="font-mono text-white font-semibold text-sm mt-0.5">
                {rates[c.code] ? `${c.symbol}${rates[c.code].toFixed(4)}` : "—"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
}
